import { C, t } from '../../../pages/Talent/Dashboard/components/dashboardConstants';

const PAGE_GROUP_SIZE = 10;

/**
 * @component GroupedPagination
 * @description 10개 단위 그룹 페이지네이션 + 화면 좌우 고정 화살표 (배우찾기 스타일)
 *
 * @param {number}   props.currentPage  - 현재 페이지 (1-based)
 * @param {number}   props.totalPages   - 전체 페이지 수
 * @param {Function} props.onPageChange - 페이지 변경 핸들러 (page: number) => void
 * @param {boolean}  [props.fixedArrows=true] - 화면 좌우 고정 화살표 버튼 표시 여부
 */
function GroupedPagination({ currentPage, totalPages, onPageChange, fixedArrows = true }) {
  if (totalPages <= 1) return null;

  const currentGroupIndex = Math.floor((currentPage - 1) / PAGE_GROUP_SIZE);
  const groupStartPage = currentGroupIndex * PAGE_GROUP_SIZE + 1;
  const groupEndPage = Math.min(totalPages, groupStartPage + PAGE_GROUP_SIZE - 1);
  const visiblePages = Array.from(
    { length: groupEndPage - groupStartPage + 1 },
    (_, index) => groupStartPage + index
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          aria-label="첫 페이지"
          style={{ width: '38px', height: '38px', borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: currentPage === 1 ? 'default' : 'pointer', opacity: currentPage === 1 ? 0.45 : 1, ...t('14px', 600, C.bk) }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: 'block', margin: '0 auto' }}>
            <path d="M18 6l-6 6 6 6" stroke={C.bk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 6l-6 6 6 6" stroke={C.bk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          onClick={() => onPageChange(Math.max(1, groupStartPage - PAGE_GROUP_SIZE))}
          disabled={groupStartPage === 1}
          aria-label="이전 그룹"
          style={{ width: '38px', height: '38px', borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: groupStartPage === 1 ? 'default' : 'pointer', opacity: groupStartPage === 1 ? 0.45 : 1, ...t('14px', 600, C.bk) }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: 'block', margin: '0 auto' }}>
            <path d="M15 18l-6-6 6-6" stroke={C.bk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {visiblePages.map(p => (
          <button key={p} onClick={() => onPageChange(p)}
            aria-label={`${p}페이지`}
            aria-current={p === currentPage ? 'page' : undefined}
            style={{ width: '38px', height: '38px', borderRadius: '10px', border: `1px solid ${p === currentPage ? C.bk : C.g200}`, background: p === currentPage ? C.bk : C.white, cursor: 'pointer', ...t('14px', 600, p === currentPage ? C.white : C.bk) }}>
            {p}
          </button>
        ))}
        <button
          onClick={() => onPageChange(Math.min(totalPages, groupEndPage + 1))}
          disabled={groupEndPage === totalPages}
          aria-label="다음 그룹"
          style={{ width: '38px', height: '38px', borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: groupEndPage === totalPages ? 'default' : 'pointer', opacity: groupEndPage === totalPages ? 0.45 : 1, ...t('14px', 600, C.bk) }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: 'block', margin: '0 auto' }}>
            <path d="M9 18l6-6-6-6" stroke={C.bk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          aria-label="마지막 페이지"
          style={{ width: '38px', height: '38px', borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: currentPage === totalPages ? 'default' : 'pointer', opacity: currentPage === totalPages ? 0.45 : 1, ...t('14px', 600, C.bk) }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: 'block', margin: '0 auto' }}>
            <path d="M6 6l6 6-6 6" stroke={C.bk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 6l6 6-6 6" stroke={C.bk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {fixedArrows && currentPage > 1 && (
        <button
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="이전 페이지"
          style={{
            position: 'fixed', left: '24px', top: '50%', transform: 'translateY(-50%)',
            width: '64px', height: '64px', borderRadius: '50%',
            border: `1px solid ${C.g200}`, background: C.white,
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke={C.bk} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {fixedArrows && currentPage < totalPages && (
        <button
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="다음 페이지"
          style={{
            position: 'fixed', right: '24px', top: '50%', transform: 'translateY(-50%)',
            width: '64px', height: '64px', borderRadius: '50%',
            border: `1px solid ${C.g200}`, background: C.white,
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke={C.bk} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </>
  );
}

export default GroupedPagination;
